import { Observable, observe, Scope } from 'wdc-cube'
import { WaitingScope } from '../../the-connection/tc_scopes'
import { PriceTierEditorScope } from '../billing/bl_scopes'
import type { PriceTableVersion } from '../billing/bl_types'
import { GroupRow } from '../group-manager/gm_scopes'
import { NotificationType, SimpleUserWithGroups } from '../user-manager/um_scopes'

export type SmtpConfig = {
    seq?: number
    host: string
    port: number
    secure?: boolean
    tls?: boolean
    user?: string
    pass?: string
    from?: string
    template_welcome_seq?: number
    template_change_password_request_seq?: number
    template_password_changed_seq?: number
}

export type MailTemplateRow = {
    seq?: number
    name: string
    subject: string
    templateType: MailTemplateOption
    templateContent: string
}

export enum NotificationStyle {
    INFO = 'INFO',
    SUCCESS = 'SUCCESS',
    WARNING = 'WARNING',
    ERROR = 'ERROR'
}

export type NotificationRow = {
    id?: number
    title: string
    summary: string
    content: string
    notificationStyle: NotificationStyle
    canMarkAsRead: boolean
    active: boolean
    alert: boolean
    typeId: number | null
    toAllUsers: boolean
    createdUserId: number
    expiresIn?: Date
    createdAt: Date
    notificationDate: Date
    groups?: NotificationGroupRow[]
    users?: NotificationUserRow[]
}

export type UpdateNotificationRow = Omit<NotificationRow, 'id' | 'createdUserId' | 'createdAt'>

export type NotificationGroupRow = {
    id?: number
    groupId: number
    groupName?: string
}

export type NotificationUserRow = {
    id?: number
    userId: number
    userName?: string
    groups?: string
    readAt?: Date
}

export type ShowNotificationRow = Omit<NotificationRow, 'groups' | 'users' | 'createdUserId'> & {
    createdUser: string
    groupsCount: number
    usersCount: number
}

export type UserNotification = {
    id: number
    title: string
    summary: string
    content: string
    type?: string
    notificationStyle: NotificationStyle
    canMarkAsRead: boolean
    expired: boolean
    alert: boolean
    readAt?: Date
    notificationDate?: Date
}

export type MailTemplateOption = 'welcome' | 'password-recovery' | 'new-password-generated'

export class MailTemplateScope extends Scope {
    @observe() waiting?: WaitingScope
    @observe() tab = 'list' as 'list' | 'form'

    @observe() templateFilter = ''

    @observe() templates = [] as MailTemplateRow[]

    @observe() templateId? = 0
    @observe() templateType = 'welcome' as MailTemplateOption
    @observe() templateName = ''
    @observe() subject = ''
    @observe() previewHtml = ''
    @observe() content = ''
    @observe() variables = [] as string[]
    @observe() availableVariables = [] as string[]

    onTemplateTypeChanged = Scope.ASYNC_ACTION_ONE<MailTemplateOption>()

    onContentChanged = Scope.ASYNC_ACTION_STRING
    onSave = Scope.ASYNC_ACTION
    onClose = Scope.ASYNC_ACTION

    onNew = Scope.ASYNC_ACTION
    onEdit = Scope.ASYNC_ACTION_NUMBER
    onDelete = Scope.ASYNC_ACTION_NUMBER
    onFind = Scope.ASYNC_ACTION_STRING

    @observe() askYesOrNo = ''
    onYesNoResponse = Scope.ASYNC_ACTION_BOOLEAN
}

export type ConfigurationTabsOption = 'email' | 'email-template' | 'notifications' | 'billing'

@Observable
export class BillingConfigScope extends Scope {
    @observe() waiting?: WaitingScope

    readonly tierEditor = new PriceTierEditorScope()

    @observe() history = [] as PriceTableVersion[]
    @observe() validFrom = ''

    onLoadData = Scope.ASYNC_ACTION
    onSave = Scope.ASYNC_ACTION
}

@Observable
export class EmailConfigScope extends Scope {
    readonly header = new HeaderScope()

    @observe() waiting?: WaitingScope

    @observe() config = {} as SmtpConfig
    @observe() templates = [] as MailTemplateRow[]

    onLoadData = Scope.ASYNC_ACTION
    onSave = Scope.ASYNC_ACTION
    onConfigChanged = Scope.ASYNC_ACTION_ONE<Partial<SmtpConfig>>()
}

export type EditableNotificationFields = Pick<
    NotificationTabScope,
    | 'title'
    | 'summary'
    | 'content'
    | 'style'
    | 'typeId'
    | 'canMarkAsRead'
    | 'active'
    | 'alert'
    | 'expiresIn'
    | 'notificationDate'
    | 'toAllUsers'
>

@Observable
export class NotificationTabScope extends Scope {
    manageTypeScope = new ManageTypesScope()

    @observe() waiting?: WaitingScope
    @observe() tab = 'list' as 'list' | 'form'

    @observe() filter = ''

    @observe() notifications = [] as ShowNotificationRow[]

    @observe() allGroups = [] as GroupRow[]
    @observe() allUsers = [] as SimpleUserWithGroups[]
    @observe() allNotificationTypes = [] as NotificationType[]

    @observe() notificationId? = 0
    @observe() title = ''
    @observe() summary = ''
    @observe() content = ''
    @observe() style = NotificationStyle.INFO
    @observe() typeId = null as number | null
    @observe() canMarkAsRead = true
    @observe() active = true
    @observe() alert = false
    @observe() toAllUsers = true
    @observe() expiresIn = undefined as Date | undefined
    @observe() notificationDate = undefined as Date | undefined
    @observe() groups = [] as NotificationGroupRow[]
    @observe() users = [] as NotificationUserRow[]
    @observe() selectedUserId = null as number | null

    onFieldChanged = Scope.ASYNC_ACTION_TWO<
        keyof EditableNotificationFields,
        EditableNotificationFields[keyof EditableNotificationFields]
    >()

    onGroupsChanged = Scope.ASYNC_ACTION_ONE<(number | undefined)[]>()
    onUsersChanged = Scope.ASYNC_ACTION_ONE<number | null>()
    onAddSelectedUser = Scope.ASYNC_ACTION

    onSave = Scope.ASYNC_ACTION
    onClose = Scope.ASYNC_ACTION

    onNew = Scope.ASYNC_ACTION
    onEdit = Scope.ASYNC_ACTION_NUMBER
    onDelete = Scope.ASYNC_ACTION_NUMBER
    onDeleteUser = Scope.ASYNC_ACTION_NUMBER
    onFind = Scope.ASYNC_ACTION_STRING

    onTypesChanged = Scope.ASYNC_ACTION

    @observe() askYesOrNo = ''
    onYesNoResponse = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class ManageTypesScope extends Scope {
    @observe() waiting?: WaitingScope
    @observe() allNotificationTypes = [] as NotificationType[]

    @observe() typeId? = 0
    @observe() name = ''
    @observe() active = true

    @observe() manageTypesOpen = false

    onNameChange = Scope.ASYNC_ACTION_STRING
    onActiveChange = Scope.ASYNC_ACTION_BOOLEAN

    onSave = Scope.ASYNC_ACTION
    onOpen = Scope.ASYNC_ACTION
    onClose = Scope.ASYNC_ACTION

    onNew = Scope.ASYNC_ACTION
    onEdit = Scope.ASYNC_ACTION_NUMBER
    onDelete = Scope.ASYNC_ACTION_NUMBER

    @observe() askYesOrNo = ''
    onYesNoResponse = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class HeaderScope extends Scope {}

@Observable
export class ConfigurationTabsScope extends Scope {
    readonly header = new HeaderScope()

    @observe() active: ConfigurationTabsOption = 'email'

    emailConfig = new EmailConfigScope()
    emailTemplate = new MailTemplateScope()
    notificationTab = new NotificationTabScope()
    billingConfig = new BillingConfigScope()

    onSelect = Scope.ASYNC_ACTION_ONE<ConfigurationTabsOption>()
}

export default ConfigurationTabsScope
